import { useClickOutside } from "@reactuses/core";
import { ChevronDown, SmilePlus } from "lucide-react";
import { motion } from "motion/react";
import type { CSSProperties, ReactNode, RefObject } from "react";
import {
    useViewportAwarePlacement,
    type ViewportAlign,
    type ViewportSide,
} from "../../../../hooks/useViewportAwarePlacement";
import { cn } from "../../../../lib/cn";
import { Pressable } from "../../../Pressable";
import { useChat } from "../../ChatContext";
import { useMessage } from "../Context";
import { ActionMenu, ReactionBar } from "./Selection";

const PANEL_EASE = [0.23, 1, 0.32, 1] as const;

export type DesktopMessagePanel = "reactions" | "actions";

type DesktopHoverButtonsProps = {
    openPanel: DesktopMessagePanel | null;
    onOpenPanel: (panel: DesktopMessagePanel | null) => void;
    containerRef: RefObject<HTMLElement | null>;
};

export function DesktopHoverButtons({
    openPanel,
    onOpenPanel,
    containerRef,
}: DesktopHoverButtonsProps) {
    const { sender, reaction } = useMessage();
    const { labels } = useChat();
    const isVisible = openPanel !== null;
    const isAgent = sender === "agent";
    const preferredAlign = isAgent ? "end" : "start";

    function dismiss() {
        onOpenPanel(null);
    }

    useClickOutside(containerRef, dismiss, openPanel !== null);

    return (
        <div
            className={cn(
                "absolute top-1/2 z-10 flex -translate-y-1/2 flex-col gap-4",
                isAgent && (reaction ? "left-full ml-24" : "left-full ml-8"),
                !isAgent && (reaction ? "right-full mr-24" : "right-full mr-8"),
                "pointer-events-none opacity-0 transition-opacity duration-150 ease-out",
                "group-hover/message:pointer-events-auto group-hover/message:opacity-100",
                isVisible && "pointer-events-auto opacity-100",
            )}
        >
            <HoverPanel
                active={openPanel === "reactions"}
                preferredAlign={preferredAlign}
                preferredSide="above"
            >
                <ReactionBar className="shadow-md" onDismiss={dismiss} />
            </HoverPanel>

            <HoverActionButton
                label={labels.addReaction}
                expanded={openPanel === "reactions"}
                onClick={() =>
                    onOpenPanel(openPanel === "reactions" ? null : "reactions")
                }
            >
                <SmilePlus aria-hidden className="size-20" />
            </HoverActionButton>

            <HoverActionButton
                label={labels.moreActions}
                expanded={openPanel === "actions"}
                onClick={() => onOpenPanel(openPanel === "actions" ? null : "actions")}
            >
                <ChevronDown aria-hidden className="size-20" />
            </HoverActionButton>

            <HoverPanel
                active={openPanel === "actions"}
                preferredAlign={preferredAlign}
                preferredSide="below"
            >
                <ActionMenu className="shadow-md" onDismiss={dismiss} />
            </HoverPanel>
        </div>
    );
}

type HoverPanelProps = {
    active: boolean;
    preferredAlign: ViewportAlign;
    preferredSide: ViewportSide;
    children: ReactNode;
};

function HoverPanel({
    active,
    preferredAlign,
    preferredSide,
    children,
}: HoverPanelProps) {
    const { ref, align, side, offset } = useViewportAwarePlacement({
        active,
        preferredAlign,
        preferredSide,
    });

    if (!active) {
        return null;
    }

    const offsetStyle: CSSProperties | undefined =
        offset.x === 0 && offset.y === 0
            ? undefined
            : { transform: `translate(${offset.x}px, ${offset.y}px)` };

    return (
        <div
            ref={ref}
            className={cn(
                "absolute flex",
                side === "above" ? "bottom-full mb-8" : "top-full mt-8",
                align === "end" ? "right-0" : "left-0",
            )}
            style={offsetStyle}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, ease: PANEL_EASE }}
            >
                {children}
            </motion.div>
        </div>
    );
}

type HoverActionButtonProps = {
    children: ReactNode;
    label: string;
    expanded: boolean;
    onClick: () => void;
};

function HoverActionButton({
    children,
    label,
    expanded,
    onClick,
}: HoverActionButtonProps) {
    return (
        <Pressable
            aria-label={label}
            aria-expanded={expanded}
            className="grid size-24 place-items-center rounded-full bg-component-chat-agent text-text-primary transition-colors duration-150 ease-out hover:bg-component-chat-member active:scale-[0.92]"
            onPress={onClick}
        >
            {children}
        </Pressable>
    );
}
